// Text catalog for the Brancher / Industries index (listing) page.
// Icons and link targets live in src/pages/BrancherIndexPage.tsx; the industry
// cards are aligned by index with the `industries` array there.

import { type Locale } from "../config";
import { type PageMeta } from "../head";

type IndustryCard = { title: string; text: string };

type BrancherStrings = {
  meta: PageMeta;
  keywords: string;
  ogTitle: string;
  ogDescription: string;
  hero: { eyebrow: string; heading: string; body: string };
  list: { heading: string; intro: string; readMore: string };
  industries: IndustryCard[];
  cta: { heading: string; body: string; cta: string };
};

export const brancherStrings: Record<Locale, BrancherStrings> = {
  da: {
    meta: {
      title: "Brancher - specialiseret bygherrerådgivning | MI Support",
      description:
        "MI Support leverer specialiseret bygge-, projekt- og sikkerhedsledelse til pharma, industri, datacentre, hospitaler, kontor og infrastruktur.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    keywords:
      "brancher, pharma byggeri, life science byggeri, GMP, industribyggeri, datacenter byggeri, hospitalsbyggeri, infrastruktur, bygherrerådgivning brancher",
    ogTitle: "Brancher - MI Support",
    ogDescription:
      "Specialiseret rådgivning til komplekse byggerier inden for pharma, industri, datacentre, hospitaler og infrastruktur.",
    hero: {
      eyebrow: "Vores brancher",
      heading: "Specialiseret rådgivning til din branche",
      body: "MI Support arbejder på tværs af nogle af Danmarks mest komplekse byggerier. Vi forstår de specifikke krav, regulativer og processer i jeres branche, og tilpasser bygge-, projekt- og sikkerhedsledelse (HSE) derefter.",
    },
    list: {
      heading: "Vores brancher",
      intro: "Vi arbejder på tværs af nogle af Danmarks mest komplekse byggerier. Find jeres branche herunder og se hvordan vi kan hjælpe.",
      readMore: "Læs mere",
    },
    industries: [
      { title: "Pharma, biotech & life science", text: "GMP-byggeri, renrum og fermentering. Vi rådgiver pharma-, biotech- og life science-bygherrer på fabrikker, laboratorier og produktionsanlæg med høje krav til kvalitet, dokumentation og myndighedsgodkendelse." },
      { title: "Industri & tung produktion", text: "Procesanlæg, fødevareproduktion og tunge industrianlæg, hvor produktion, sikkerhed og driftsstabilitet skal gå hånd i hånd med byggeprojektets fremdrift." },
      { title: "Datacentre & driftskritisk IT", text: "Driftskritisk byggeri med ekstreme krav til oppetid, køling, strøm og commissioning. Vi har erfaring fra hyperscale-projekter i Danmark og Norden." },
      { title: "Hospitaler & sundhedsbyggeri", text: "Hospitalsbyggeri og sundhedsbygninger med komplekse brugerflader, hygiejnekrav og tekniske installationer." },
      { title: "Domiciler & kontorbyggeri", text: "Domicil- og kontorbyggeri med fokus på arkitektur, bæredygtighed (DGNB), brugerinvolvering og fleksible arbejdsmiljøer." },
      { title: "Energi & forsyningsanlæg", text: "Anlægs- og forsyningsprojekter inden for bane, vej, vand og energi, med stærk koordinering mellem entreprenører, myndigheder og driftsorganisation." },
      { title: "Transport & logistikanlæg", text: "Terminaler, baneanlæg, havne, vejprojekter og logistikcentre, komplekse anlægsprojekter med mange interessenter, trafikafvikling og myndighedsgodkendelser." },
    ],
    cta: {
      heading: "Specialiseret rådgivning til netop jeres branche",
      body: "Uanset branche har vi erfaring med kravene til dokumentation, myndigheder, sikkerhed og kvalitet. Lad os tage en uforpligtende snak om jeres næste projekt.",
      cta: "Kontakt os",
    },
  },

  en: {
    meta: {
      title: "Industries - specialised client consultancy | MI Support",
      description:
        "MI Support delivers specialised construction, project and safety management for pharma, industry, data centers, hospitals and infrastructure.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    keywords:
      "industries, pharma construction, life science construction, GMP, industrial construction, data center construction, hospital construction, infrastructure, client consultancy industries",
    ogTitle: "Industries - MI Support",
    ogDescription:
      "Specialised consultancy for complex construction within pharma, industry, data centers, hospitals and infrastructure.",
    hero: {
      eyebrow: "Our industries",
      heading: "Specialised consultancy for your industry",
      body: "MI Support works across some of Denmark's most complex construction projects. We understand the specific requirements, regulations and processes in your industry, and adapt our construction, project and safety management (HSE) accordingly.",
    },
    list: {
      heading: "Our industries",
      intro: "We work across some of Denmark's most complex construction projects. Find your industry below and see how we can help.",
      readMore: "Read more",
    },
    industries: [
      { title: "Pharma, biotech & life science", text: "GMP construction, cleanrooms and fermentation. We advise pharma, biotech and life science clients on factories, laboratories and production facilities with high demands for quality, documentation and regulatory approval." },
      { title: "Industry & heavy manufacturing", text: "Process plants, food production and heavy industrial facilities, where production, safety and operational stability must go hand in hand with the progress of the construction project." },
      { title: "Data centers & mission-critical IT", text: "Mission-critical construction with extreme demands for uptime, cooling, power and commissioning. We have experience from hyperscale projects in Denmark and the Nordics." },
      { title: "Hospitals & healthcare construction", text: "Hospital construction and healthcare buildings with complex user interfaces, hygiene requirements and technical installations." },
      { title: "Headquarters & office construction", text: "Headquarters and office construction with a focus on architecture, sustainability (DGNB), user involvement and flexible work environments." },
      { title: "Energy & utility facilities", text: "Infrastructure and utility projects within rail, road, water and energy, with strong coordination between contractors, authorities and the operations organisation." },
      { title: "Transport & logistics facilities", text: "Terminals, rail facilities, ports, road projects and logistics centres – complex infrastructure projects with many stakeholders, traffic management and regulatory approvals." },
    ],
    cta: {
      heading: "Specialised consultancy for exactly your industry",
      body: "Whatever the industry, we have experience with the requirements for documentation, authorities, safety and quality. Let us have a no-obligation chat about your next project.",
      cta: "Contact us",
    },
  },

  de: {
    meta: {
      title: "Branchen - spezialisierte Bauherrenberatung | MI Support",
      description:
        "MI Support liefert spezialisiertes Bau-, Projekt- und Sicherheitsmanagement für Pharma, Industrie, Rechenzentren, Krankenhäuser und Infrastruktur.",
      ogImage: "https://www.misupport.dk/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg",
    },
    keywords:
      "Branchen, Pharmabau, Life-Science-Bau, GMP, Industriebau, Rechenzentrumsbau, Krankenhausbau, Infrastruktur, Bauherrenberatung Branchen",
    ogTitle: "Branchen - MI Support",
    ogDescription:
      "Spezialisierte Beratung für komplexe Bauten in Pharma, Industrie, Rechenzentren, Krankenhäusern und Infrastruktur.",
    hero: {
      eyebrow: "Unsere Branchen",
      heading: "Spezialisierte Beratung für Ihre Branche",
      body: "MI Support arbeitet branchenübergreifend an einigen der komplexesten Bauvorhaben Dänemarks. Wir verstehen die spezifischen Anforderungen, Vorschriften und Prozesse Ihrer Branche und passen unser Bau-, Projekt- und Sicherheitsmanagement (HSE) entsprechend an.",
    },
    list: {
      heading: "Unsere Branchen",
      intro: "Wir arbeiten branchenübergreifend an einigen der komplexesten Bauvorhaben Dänemarks. Finden Sie Ihre Branche unten und sehen Sie, wie wir helfen können.",
      readMore: "Mehr erfahren",
    },
    industries: [
      { title: "Pharma, Biotech & Life Science", text: "GMP-Bau, Reinräume und Fermentation. Wir beraten Pharma-, Biotech- und Life-Science-Bauherren bei Fabriken, Laboren und Produktionsanlagen mit hohen Anforderungen an Qualität, Dokumentation und behördliche Zulassung." },
      { title: "Industrie & Schwerproduktion", text: "Prozessanlagen, Lebensmittelproduktion und schwere Industrieanlagen, bei denen Produktion, Sicherheit und Betriebsstabilität mit dem Fortschritt des Bauprojekts Hand in Hand gehen müssen." },
      { title: "Rechenzentren & betriebskritische IT", text: "Betriebskritischer Bau mit extremen Anforderungen an Verfügbarkeit, Kühlung, Strom und Commissioning. Wir verfügen über Erfahrung aus Hyperscale-Projekten in Dänemark und in der nordischen Region." },
      { title: "Krankenhäuser & Gesundheitsbau", text: "Krankenhausbau und Gesundheitsgebäude mit komplexen Nutzerschnittstellen, Hygieneanforderungen und technischen Installationen." },
      { title: "Hauptsitze & Bürobau", text: "Hauptsitz- und Bürobau mit Fokus auf Architektur, Nachhaltigkeit (DGNB), Nutzerbeteiligung und flexible Arbeitsumgebungen." },
      { title: "Energie- & Versorgungsanlagen", text: "Infrastruktur- und Versorgungsprojekte in den Bereichen Schiene, Straße, Wasser und Energie, mit starker Koordination zwischen Auftragnehmern, Behörden und Betriebsorganisation." },
      { title: "Transport- & Logistikanlagen", text: "Terminals, Bahnanlagen, Häfen, Straßenprojekte und Logistikzentren – komplexe Infrastrukturprojekte mit vielen Interessengruppen, Verkehrsabwicklung und behördlichen Genehmigungen." },
    ],
    cta: {
      heading: "Spezialisierte Beratung für genau Ihre Branche",
      body: "Unabhängig von der Branche verfügen wir über Erfahrung mit den Anforderungen an Dokumentation, Behörden, Sicherheit und Qualität. Lassen Sie uns ein unverbindliches Gespräch über Ihr nächstes Projekt führen.",
      cta: "Kontaktieren Sie uns",
    },
  },
};
